import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  ParticipationBaseBarrierEnum,
  ParticipationBaseEnum,
  ParticipationEnum,
  PayoffEnum,
  ProductCaptureGroupType,
  ProductCaptureGroupsEnum,
  ProductNotesEnum,
  ProductTypeEnum,
  ScheduleType,
  SummaryEnum,
} from '../model/enums/product-capture.enums';

import { Observable, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { applyValueToFieldPath } from 'src/app/form-config/helpers/get-options-by-field-name';
import {
  SUMMARY_PERFORMANCE_TYPE,
  SUMMARY_PRODUCT_TYPE,
  SUMMARY_SETTLEMENT_TYPE,
} from 'src/app/form-config/mockups/consts/product-capture-field-selectors.consts';
import { CLNSettingsService } from 'src/app/form-config/model/product-settings/cln.settings';
import { ProductSettingsService } from 'src/app/form-config/product-settings.service';
import { countriesOfOfferSubscriptionRequiredValidator } from 'src/app/form-config/validators/countries-of-offer-subscriptions-required-validator';
import { contactInformationValidator } from 'src/app/form-config/validators/cross-field-validators/contact-information.validator';
import { countriesOfOfferDatesValidator } from 'src/app/form-config/validators/cross-field-validators/countries-of-offer-dates-validator';
import { Agent } from 'src/app/static-data/models/consts/agents.static-data';
import { AgentStaticDataService } from 'src/app/static-data/services/agents-static-data.service';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { ModalService } from 'src/app/ui-kit/dc-modal/modal.service';
import { FormConfigService } from '../../form-config/form-config.service';
import { KeyDateDetail } from '../model/classes/key-date-detail';
import {
  PRODUCT_CAPTURE_FORM_CONST,
  PRODUCT_CAPTURE_FORM_RESET_DEFAULTS,
} from '../model/consts/product-capture-form.const';
import { PRODUCT_CAPTURE_CONST } from '../model/consts/product-capture-key.const';
import { getValidationForCodeType } from '../model/consts/product-identifier-validation.const';
import { KeyDatesSnapshot } from '../model/interfaces/key-dates-snapshot.interface';
import {
  ProductCaptureData,
  Task,
} from '../model/interfaces/product-capture.interface';
import { CanComponentDeactivate } from '../services/can-deactivate-guard';
import { ProductCaptureService } from '../services/product-capture.service';
import { GetErrorMessageFromResponse } from '../utils/get-error-message-from-response';
import { GetFormInvalidFields } from '../utils/get-invalid-fields';
import { GetLabelFromSettings } from '../utils/get-label';
import { GetProductCaptureFormPatchValue } from '../utils/get-product-capture-form-patch-value';
import { GetProductCapturePostData } from '../utils/get-product-capture-post-data';
import { ProductUtils } from '../utils/product.utils';
import { CommonProductCaptureDataService } from './common-product-capture-data.service';
import {
  CREATE_PRODUCT_BUTTONS,
  ProductButtonMap,
} from './create-product.const';
import { CreditProvisionService } from './credit-provision/credit-provision.service';
import { EventSchedulesComponent } from './event-schedules/event-schedules.component';
import { ScheduleComponent } from './payoff/schedule/schedule.component';
import { KeyDatesModalService } from './product-summary/key-dates-modal/key-dates-modal.service';
import { KeyDatesSnapshotService } from './product-summary/key-dates-snapshot.service';
import { WarningModalService } from './warning-modal/warning-modal.service';

const controls = 'controls';

@Component({
  selector: 'app-create-product',
  templateUrl: './create-product.component.html',
  styleUrls: ['./create-product.component.scss'],
  providers: [
    FormConfigService,
    CreditProvisionService,
    CommonProductCaptureDataService,
    KeyDatesModalService,
    ProductSettingsService,
    CLNSettingsService,
  ],
})
export class CreateProductComponent
  implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate
{
  form = new UntypedFormGroup(PRODUCT_CAPTURE_FORM_CONST);

  id: any;

  invalidFormMessage =
    'There are some errors in the form. Please correct them before submitting.';
  loading = true;

  mainSection: ProductCaptureGroupType;

  pageTitle = 'Product';

  path = '';

  prod: ProductCaptureData;

  formName = PRODUCT_CAPTURE_CONST + new Date().toTimeString();

  destroy$ = new Subject();

  canUserEdit = true;

  sectionButtons: ProductButtonMap = CREATE_PRODUCT_BUTTONS;

  originalOrder = () => 0;

  ignoredPaths: [];

  productTypeValue: ProductTypeEnum;

  @ViewChild(EventSchedulesComponent)
  eventScheduleComponent: EventSchedulesComponent;

  constructor(
    private cd: ChangeDetectorRef,
    private dcAlertService: DcAlertService,
    private formConfigService: FormConfigService,
    private productCaptureService: ProductCaptureService,
    private router: Router,
    private route: ActivatedRoute,
    private warningModalService: WarningModalService,
    private agentStaticDataService: AgentStaticDataService,
    private modalService: ModalService,
    private productSettingsService: ProductSettingsService,
    private keyDatesSnapshotService: KeyDatesSnapshotService,
    private ngZone: NgZone
  ) {
    this.formConfigService.setFormConfig(this.formName);
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        this.id = params.get('id');
        this.mainSection = ProductCaptureGroupsEnum.payoff;
        this.path = this.router.url.split('/')[2];
        this.initForm();
      });
    this.getSummaryFormGroup
      .get('productType')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.productTypeValue = value;

        const hiddenSections =
          this.productSettingsService.getHiddenSectionsByProductType(value);

        if (hiddenSections?.length) {
          this.sectionButtons.forEach(
            (button, key) => (button.hidden = hiddenSections.includes(key))
          );
        }

        if (value !== 'CLN') {
          if (this.mainSection === 'creditProvision') {
            this.changeMainSection('payoff');
          }
        }

        this.productSettingsService
          .getProductSettingsService(value)
          ?.setDefaultSettingsForDB({
            form: this.form, 
            schedules: this.schedules,
            addAgentRow: this.addAgentRow.bind(this),
          });
      });
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.formConfigService.stop(this.formName);
    this.form.reset(PRODUCT_CAPTURE_FORM_RESET_DEFAULTS, { emitEvent: false });
    this.prod = null;

    if (this.getSalesFeesArr) {
      this.getSalesFeesArr.clear();
    }
    if (this.getProductIdentifiersArr) {
      this.getProductIdentifiersArr.clear();
    }
    if (this.countriesOfOfferArr) {
      this.countriesOfOfferArr.clear();
    }
    if (this.getAgentsArray) {
      this.getAgentsArray.clear();
    }
    if (this.getTargetMarketArr) {
      this.getTargetMarketArr.clear();
    }
    if (this.getContactInformationRows) {
      this.getContactInformationRows?.clear();
    }
    if (this.getDropBackBarrierLevels) {
      this.getDropBackBarrierLevels.clear();
    }
    if (this.form) {
      this.clearIgnoredErrors(GetFormInvalidFields(this.form));
    }

    this.clearSchedules();
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearSchedules(): void {
    this.clearScheduleArrayByPath(ScheduleType.COUPON);
    this.clearScheduleArrayByPath(ScheduleType.CALLABILITY);
    this.clearScheduleArrayByPath(ScheduleType.PARTICIPATION, 'downside');
    this.clearScheduleArrayByPath(ScheduleType.PARTICIPATION, 'upside');
    this.clearScheduleArrayByPath(ScheduleType.PARTICIPATION, 'base');
  }

  clearScheduleArrayByPath(...path: string[]): void {
    const scheduleGroup = this.getSummaryFormGroup?.get(
      SummaryEnum.schedule
    ) as FormGroup;
    const scheduleArray = scheduleGroup?.get(
      `${path.join('.')}.schedule`
    ) as FormArray;
    scheduleArray?.clear();
  }

  initForm(): void {
    if (this.id === 'new') {
      // If creating a new product
      this.loading = false;

      // Disable and then immediately enable the form to trigger validation
      this.form.disable();
      this.form.enable();

      // Add rows and configure form
      this.addAgentRow();
      this.addCountriesOfOfferRow();
      this.addLegalProductIdRow();
      this.addSalesFeesRow();
      this.addTargetMarketRow();
      this.addContactInformationRow();

      // Start form configuration service
      this.addTasks();
      this.formConfigService.start(this.form, this.formName, true);

      // Set page title
      this.pageTitle = 'New Product';

      // Save the initial form value
      this.prod = this.form.value;

      // Toggle read-only on fields
      this.toggleReadOnlyOnFields(this.form, false);
    } else {
      // If editing an existing product
      this.loading = true;

      // Fetch currencies before getting product details
      this.productCaptureService
        .getCurrencies()
        .pipe(takeUntil(this.destroy$))
        .subscribe((currencies) => {
          this.getProductDetail();
        });
    }
  }

  private getProductDetail(): void {
    this.productCaptureService
      .getProduct(this.id as unknown as number)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => (this.loading = false))
      )
      .subscribe(
        (product) => {
          this.editProduct(product);
        },
        (error) => {
          this.loading = false;
          if (error.status == 404) {
            this.pageTitle = this.isEditing()
              ? 'Edit Product - Product Not Found'
              : 'Clone Product - Clone Not Found';
            this.dcAlertService.showAlert({
              type: 'danger',
              message: 'Product Not Found',
            });
          } else {
            this.dcAlertService.showAlert({
              type: 'danger',
              message: error.error.message,
            });
            console.log(error);
          }
        }
      );
  }

  private editProduct(prod: ProductCaptureData): void {
    this.toggleReadOnlyOnFields(this.form, false);
    this.pageTitle = 'Edit Product';

    if (this.isEditing()) {
      prod?.legalForm?.productIdentifiers?.productIdentifiersArr.forEach(() =>
        this.addLegalProductIdRow()
      );
    }

    prod?.legalForm?.countriesOfOffer?.countriesOfOfferArr.forEach((row) =>
      this.addCountriesOfOfferRow(row)
    );

    prod?.sales?.feesGroup?.feesArr.forEach(() => this.addSalesFeesRow());
    prod?.sales?.targetMarkets.forEach(() => this.addTargetMarketRow()); // TODO: ensure this field is disabled for Traded products!
    prod?.sales?.contactInformation?.forEach(() =>
      this.addContactInformationRow()
    );

    const levelsAndInvestments =
      prod?.payoff?.participation?.base?.barrier?.levelsAndInvestments ?? [];
    levelsAndInvestments.forEach(() => this.addDropBackBarrierLevelRow());

    const post = GetProductCaptureFormPatchValue(prod, this.isEditing());
    this.form.patchValue(post);
    this.prod = post;

    if (prod?.agents?.agentsArr?.length) {
      this.agentStaticDataService.agents$
        .pipe(takeUntil(this.destroy$), take(1))
        .subscribe((agents: Agent[]) => {
          prod.agents.agentsArr.forEach((agent: number) =>
            this.addAgentRow(agent, agents)
          );
        });
    } else {
      this.addAgentRow();
    }

    this.addTasks();

    this.formConfigService.start(this.form, this.formName);

    this.resetPerformanceAndSettlementToDefaultIfRequired();

    this.loading = true;
    this.productCaptureService
      .canUserEditProduct(this.prod)
      .subscribe((canEdit: boolean) => {
        this.canUserEdit = canEdit;
        !canEdit && this.toggleReadOnlyOnFields(this.form, true);
        this.loading = false;
      });
  }

  addTasks(): void {
    this.tasks.clear();

    if (this.prod?.notesAndTasks?.tasks?.length) {
      this.prod.notesAndTasks.tasks.forEach((task: Task) => {
        this.tasks.push(
          new FormGroup({
            description: new UntypedFormControl(task.description),
            status: new UntypedFormControl(task.status),
            dueDate: new UntypedFormControl(task.dueDate),
          })
        );
      });
    } else {
      this.tasks.push(
        new FormGroup({
          description: new UntypedFormControl(),
          status: new UntypedFormControl(),
          dueDate: new UntypedFormControl(),
        })
      );
    }
  }

  resetPerformanceAndSettlementToDefaultIfRequired(): void {
    this.resetToDefaultIfRequired(
      SUMMARY_PERFORMANCE_TYPE,
      SummaryEnum.performanceType
    );
    this.resetToDefaultIfRequired(
      SUMMARY_SETTLEMENT_TYPE,
      SummaryEnum.settlementType
    );
  }

  private resetToDefaultIfRequired(
    fieldReference: string[],
    fieldName: string
  ): void {
    const field = this.getSummaryFormGroup.get(fieldName);
    const defaultValue =
      this.formConfigService.getDefaultValueForFieldByProductType(
        fieldReference,
        this.productTypeValue
      )?.[0];
    const notDefaulted = field['readOnly'] && field.value !== defaultValue;

    if (notDefaulted) {
      field.setValue(defaultValue ?? field.value);
    }
  }

  toggleReadOnlyOnFields(form: FormGroup | FormArray, readOnly: boolean): void {
    const controls = Object.keys(form.controls);
    controls.forEach((key: string, index: number) => {
      const control = form.get(key) ?? (form as UntypedFormArray).at(index);

      if (
        control instanceof UntypedFormGroup ||
        control instanceof UntypedFormArray
      ) {
        this.toggleReadOnlyOnFields(control, readOnly);
      } else if (control instanceof AbstractControl) {
        control['readOnly'] = readOnly;
      }
    });
  }

  getLabel(fieldNames: string[]): string {
    return GetLabelFromSettings(fieldNames);
  }

  clearIgnoredErrors(invalidControls): void {
    if (!invalidControls) {
      return;
    }
    const ignoredLabels = ['productIdentifiers'];
    const ignoredPaths =
      invalidControls?.filter((path) =>
        ignoredLabels.some((label) => path.includes(label))
      ) || [];
    if (this.form) {
      ignoredPaths.forEach((path) => {
        const control = this.form.get(path);
        if (control) {
          control.setErrors(null);
        }
      });
    }
    this.ignoredPaths = ignoredPaths;
  }

  saveProduct(): void {
    const invalidControls = GetFormInvalidFields(this.form);
    this.clearIgnoredErrors(invalidControls);
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.form.get('eventSchedules').get('schedules').setErrors(null);

    const snapshotComparisonResult =
      this.keyDatesSnapshotService.compareSnapshot(
        this.prepareKeyDatesSnapshot()
      );

    if (snapshotComparisonResult.isSnapshotObsolete) {
      this.dcAlertService.showAlert({
        type: 'danger',
        message: snapshotComparisonResult.message,
      });

      return;
    }

    if (this.form.invalid) {
      const invalidControls = GetFormInvalidFields(this.form);
      const labels = [];
      invalidControls?.forEach((path) =>
        labels.push({
          label: GetLabelFromSettings(path),
          path,
        })
      );

      if (labels.length > 0) {
        this.warningModalService
          .openDialog(labels)
          ?.then((path) => {
            if (path && path[0] !== 'summary') {
              this.changeMainSection(path[0] as ProductCaptureGroupsEnum);
            }
          })
          .catch((err) => {
            console.log(err);
            this.ignoredPaths?.forEach((path) => {
              this.form.get(path)?.updateValueAndValidity();
            });
          });

        return;
      }
    }

    this.loading = true;

    const data = ProductUtils.processData(
      this.form.getRawValue()
    ).composeProduct(
      this.formConfigService.removeHiddenFieldsFromData.bind(
        this.formConfigService
      ),
      this.setSchedules.bind(this),
      this.applyDefaultValueIfHidden.bind(this),
      GetProductCapturePostData
    );

    this.isEditing() ? this.updateProduct(data) : this.createNewProduct(data);
  }

  setSchedules(data: ProductCaptureData): ProductCaptureData {
    if (!this.schedules?.length) {
      return data;
    }

    return this.schedules.reduce((acc, schedule: ScheduleComponent) => {
      return {
        ...acc,
        summary: {
          ...acc.summary,
          schedule: {
            ...acc.summary.schedule,
            [schedule.key]: {
              ...acc.summary.schedule[schedule.key],
              schedule: schedule?.getSchedule()?.value ?? [],
            },
          },
        },
      };
    }, data);
  }

  applyDefaultValueIfHidden(data: ProductCaptureData): ProductCaptureData {
    this.form.patchValue(data, { emitEvent: false });

    const options = this.formConfigService.getOptionsForField(
      SUMMARY_PRODUCT_TYPE,
      this.formName
    );
    const effects = this.formConfigService.getEffectsFromOptionById(
      options,
      this.getSummaryFormGroup.get('productType').value
    );

    this.formConfigService.hiddenFields.forEach(
      (_: AbstractControl, field: string) => {
        const selectedValues = this.formConfigService.getSelectValuesForField(
          field,
          effects
        );
        if (selectedValues.length) {
          const fieldValue = this.form.get(field).value;
          if (fieldValue !== selectedValues[0]) {
            applyValueToFieldPath(field.split('.'), data, selectedValues[0]);
          }
        }
      }
    );

    return data;
  }

  private updateProduct(product: ProductCaptureData): void {
    product.id = this.id;

    this.productCaptureService
      .updateProduct(product)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.dcAlertService.showAlert({
            type: 'success',
            message: `Product updated.`,
          });

          this.ngZone.run(() => {
            setTimeout(() => {
              this.resetFormAndNavigate();
            }, 3000); // 3 seconds
          });
        },
        (error) => {
          this.loading = false;

          this.dcAlertService.showAlert({
            type: 'danger',
            message: `Failed to create product: ${GetErrorMessageFromResponse(
              error
            )}`,
          });
          console.log(error);
        }
      );
  }

  private createNewProduct(product: ProductCaptureData): void {
    this.productCaptureService
      .createProduct(product)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (response) {
            this.dcAlertService.showAlert({
              type: 'success',
              message: `Product created.`,
            });

            this.ngZone.run(() => {
              setTimeout(() => {
                this.resetFormAndNavigate();
              }, 3000); // 3 seconds
            });
          } else {
            this.loading = false;

            this.dcAlertService.showAlert({
              type: 'danger',
              message: `Failed to create product.`,
            });
          }
        },
        (error) => {
          this.loading = false;

          this.dcAlertService.showAlert({
            type: 'danger',
            message: `Failed to create product: ${GetErrorMessageFromResponse(
              error
            )}`,
          });
          console.log(error);
        }
      );
  }

  canDeactivate(): Observable<boolean> | boolean | Promise<boolean> {
    if (this.form.dirty && this.form.touched) {
      return this.showConfirmationDialog();
    }
    return true;
  }

  navigateBack(): void {
    this.formConfigService.stop(this.formName);
    this.router.navigate(['/product-capture']);
  }

  private showConfirmationDialog(): Promise<boolean> {
    const productName = this.form.controls.summary.value.productName || '';

    return this.modalService
      .openDialog({
        title: 'Cancel Confirmation',
        body: `<p>You have unsaved changes${
          productName ? ` on ${productName}` : ''
        }.</p>
      <p>Are you sure you want to cancel?</p>`,
        buttons: {
          positive: 'Yes',
          negative: 'No',
        },
        size: 'md',
      })
      .then((result) => result === '{"positive":1}');
  }

  resetFormAndNavigate(): void {
    this.prod = null;
    this.form.reset(PRODUCT_CAPTURE_FORM_RESET_DEFAULTS, { emitEvent: false });
    this.navigateBack();
  }

  changeMainSection(section: ProductCaptureGroupType): void {
    this.mainSection = section;
  }

  scheduleOutput(e): void {
    if (e !== null) {
      if (e.type === 'callability') {
        this.getPayoffFormGroup.controls.callability[
          controls
        ].schedule.setValue({
          start: e.start,
          end: e.end,
          fixingDate: e.fixingDate,
          paymentDate: e.payment,
        });
      }

      if (e.type === 'coupon') {
        this.getPayoffFormGroup.controls.coupon[controls].schedule.setValue({
          start: e.start,
          end: e.end,
          fixingDate: e.fixingDate,
          paymentDate: e.payment,
        });
      }
    }
  }

  salesScheduleOutput(obj): void {
    this.getSalesFeesArr.controls[obj.index][controls].schedule.setValue({
      paymentDate: obj.e.payment,
    });
  }

  // DYNAMIC FORM FIELDS LOGIC

  get getSummaryFormGroup(): UntypedFormGroup {
    return this.getFormByKey('summary');
  }

  get getSalesFormGroup(): UntypedFormGroup {
    return this.getFormByKey('sales');
  }

  get getSalesFeesGroup(): UntypedFormGroup {
    return this.getSalesFormGroup?.get('feesGroup') as UntypedFormGroup;
  }

  get getSalesFeesArr(): UntypedFormArray {
    return this.getSalesFeesGroup?.get('feesArr') as UntypedFormArray;
  }

  get tasks(): FormArray {
    return this.form
      .get(ProductCaptureGroupsEnum.notesAndTasks)
      .get(ProductNotesEnum.tasks) as FormArray;
  }

  addSalesFeesRow(): void {
    this.getSalesFeesArr.push(this.createSalesFeesRow());
  }

  createSalesFeesRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      feeType: new UntypedFormControl(null),
      currencyFee: new UntypedFormControl(null),
      amountFee: new UntypedFormControl(null),
      schedule: new UntypedFormControl(null),
    });
  }

  deleteSalesFeesRow(index): void {
    this.getSalesFeesArr.removeAt(index);
  }

  get getTargetMarketArr(): UntypedFormArray {
    return this.getSalesFormGroup?.get('targetMarkets') as UntypedFormArray;
  }

  addTargetMarketRow(): void {
    this.getTargetMarketArr.push(this.createTargetMarketRow());
  }

  createTargetMarketRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      clientType: new UntypedFormControl(null),
      knowledgeAndExperience: new UntypedFormControl(),
      distributionStrategy: new UntypedFormControl(null),
    });
  }

  deleteTargetMarketRow(index): void {
    this.getTargetMarketArr.removeAt(index);
  }

  get getContactInformationRows(): UntypedFormArray {
    return this.getSalesFormGroup?.get(
      'contactInformation'
    ) as UntypedFormArray;
  }

  addContactInformationRow(): void {
    this.getContactInformationRows.push(this.createContactInformationRow());
  }

  createContactInformationRow(): UntypedFormGroup {
    return new UntypedFormGroup(
      {
        deskId: new UntypedFormControl(null),
        contactId: new UntypedFormControl(null),
        email: new UntypedFormControl({ value: null, disabled: true }),
        location: new UntypedFormControl({ value: null, disabled: true }),
        contactInformation: new UntypedFormControl(null),
      },
      [contactInformationValidator()]
    );
  }

  deleteContactInformationRow(index): void {
    this.getContactInformationRows.removeAt(index);
  }

  getFormByKey(key: string): UntypedFormGroup {
    return this.form.get(key) as UntypedFormGroup;
  }

  get getPayoffFormGroup(): UntypedFormGroup {
    return this.getFormByKey('payoff');
  }

  get getLegalFormFormGroup(): UntypedFormGroup {
    return this.getFormByKey('legalForm');
  }

  get getProductIdentifiersFormGroup(): UntypedFormGroup {
    return this.getLegalFormFormGroup?.get(
      'productIdentifiers'
    ) as UntypedFormGroup;
  }

  get getProductIdentifiersArr(): UntypedFormArray {
    return this.getProductIdentifiersFormGroup?.get(
      'productIdentifiersArr'
    ) as UntypedFormArray;
  }

  addLegalProductIdRow(): void {
    this.getProductIdentifiersArr.push(this.createLegalProductIdRow());
  }

  createLegalProductIdRow(): UntypedFormGroup {
    const group = new UntypedFormGroup({
      typeProdIdentifiers: new UntypedFormControl(null),
      codeProdIdentifiers: new UntypedFormControl(null),
    });

    const typeField = group.get('typeProdIdentifiers');
    const codeField = group.get('codeProdIdentifiers');

    typeField.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((codeType) => {
        codeField.clearValidators();

        const validationData = getValidationForCodeType(codeType);
        if (validationData) {
          codeField.setValidators([
            validationData.regexPattern
              ? Validators.pattern(validationData.regexPattern)
              : validationData.validatorFn,
          ]);
        }

        codeField.markAsTouched();
        codeField.updateValueAndValidity({ emitEvent: false });
      });

    return group;
  }

  deleteLegalProductIdRow(index): void {
    this.getProductIdentifiersArr.removeAt(index);
  }

  get countriesOfOfferArr(): UntypedFormArray {
    const legalForm = this.form.get('legalForm');
    const countriesOfOffer = legalForm
      ? legalForm.get('countriesOfOffer')
      : null;

    return countriesOfOffer
      ? (countriesOfOffer.get('countriesOfOfferArr') as UntypedFormArray)
      : null;
  }

  addCountriesOfOfferRow(row?: any): void {
    this.countriesOfOfferArr.push(this.createCountriesOfOfferRow(row));
  }

  createCountriesOfOfferRow(row?: any): UntypedFormGroup {
    return new UntypedFormGroup(
      {
        jurisdictionsLegal: new UntypedFormControl(null),
        formOfOfferLegal: new UntypedFormControl('PRIVATE'),
        subscription: new UntypedFormControl(false),
        subscriptions: new UntypedFormArray(
          new Array(row?.subscriptions?.length ?? 0).fill(null).map(
            () =>
              new UntypedFormGroup({
                subStartDate: new UntypedFormControl(null),
                subEndDate: new UntypedFormControl(null),
                isaEndDate: new UntypedFormControl(null),
                timezone: new UntypedFormControl(null),
                timezones: new UntypedFormControl([]),
                distributionChannel: new UntypedFormControl(null),
              })
          ),
          {
            validators: countriesOfOfferSubscriptionRequiredValidator(
              'subStartDate',
              'subEndDate'
            ),
          }
        ),
      },
      countriesOfOfferDatesValidator('subStartDate', 'subEndDate')
    );
  }

  deleteCountriesOfOfferRow(index): void {
    this.countriesOfOfferArr.removeAt(index);
  }

  get getAgentsFormGroup(): UntypedFormGroup {
    return this.getFormByKey('agents');
  }

  get getAgentsArray(): UntypedFormArray {
    return this.getAgentsFormGroup?.get('agentsArr') as UntypedFormArray;
  }

  addAgentRow(agentId?: number, agents?: Agent[]): void {
    this.getAgentsArray.push(this.createAgentRow(agentId, agents));
  }

  createAgentRow(agentIdNumber?: number, agents?: Agent[]): UntypedFormGroup {
    let agentData = {};
    if (this.prod && agentIdNumber && agents) {
      const agent = agents.find(
        (findAgent: Agent) => findAgent.id === agentIdNumber
      );
      if (!agent) {
        return;
      }

      const { id: agentId, type: id, entityName, lei } = agent;

      agentData = {
        id,
        agentId,
        entityName,
        lei,
      };
    }

    const agentRow = new UntypedFormGroup({
      entityName: new UntypedFormControl(null),
      lei: new UntypedFormControl(null),
      id: new UntypedFormControl(null),
      agentId: new UntypedFormControl(null),
    });

    agentRow.patchValue(agentData, { emitEvent: false });

    agentRow
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((agentType: string) => {
        this.subscribeAndGetAgentsData((agents: Agent[]) => {
          agentRow.patchValue(
            {
              agentId: null,
              entityName: null,
              lei: null,
            },
            { emitEvent: false }
          );

          const filteredAgents = agents.filter(
            (agent: Agent) => agent.type === agentType
          );

          if (filteredAgents.length === 1) {
            const agent = filteredAgents[0];
            agentRow.patchValue(
              {
                entityName: agent.entityName,
                lei: agent.lei,
                agentId: agent.id,
              },
              { emitEvent: false }
            );
          }
        });
      });

    agentRow
      .get('entityName')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((entityName: string) => {
        if (entityName != undefined) {
          this.subscribeAndGetAgentsData((agents: Agent[]) => {
            const { value: agentType } = agentRow.get('id');
            const { productId, ...agent } = agents?.find(
              (agent: Agent) =>
                agent.entityName === entityName && agent.type === agentType
            );
            if (!agent) {
              return;
            }

            agentRow.patchValue(
              { lei: agent.lei, agentId: agent.id },
              { emitEvent: false }
            );
          });
        }
      });

    return agentRow;
  }

  subscribeAndGetAgentsData(callback: (agent: Agent[]) => void): void {
    this.agentStaticDataService.agents$.pipe(take(1)).subscribe(callback);
  }

  deleteAgentRow(index): void {
    this.getAgentsArray.removeAt(index);
    if (this.getAgentsArray.length === 0) {
      this.addAgentRow();
    }
  }

  get getDropBackBarrierLevels(): UntypedFormArray {
    return this.getPayoffFormGroup
      ?.get(PayoffEnum.participation)
      ?.get(ParticipationEnum.base)
      ?.get(ParticipationBaseEnum.barrier)
      ?.get(
        ParticipationBaseBarrierEnum.levelsAndInvestments
      ) as UntypedFormArray;
  }

  addDropBackBarrierLevelRow(): void {
    this.getDropBackBarrierLevels.push(this.createDropBackBarrierLevelRow());
  }

  createDropBackBarrierLevelRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      level: new UntypedFormControl(null),
      investmentAllocation: new UntypedFormControl(null),
    });
  }

  isEditing(): boolean {
    return this.path === 'edit-product';
  }

  private prepareKeyDatesSnapshot(): KeyDatesSnapshot {
    const keyDateGroup = this.getSummaryFormGroup.get('keyDates');

    return {
      tradingCalendars: this.getSummaryFormGroup.get('tradingCalendars').value,
      settlementCalendars: this.getSummaryFormGroup.get('settlementCalendars')
        .value,
      tenor: this.getSummaryFormGroup.get('tenor').value,
      issueDate: new KeyDateDetail(
        keyDateGroup.get('issueDate').get('value').value,
        keyDateGroup.get('issueDate').get('priority').value,
        keyDateGroup.get('issueDate').get('anchorDate').value,
        keyDateGroup.get('issueDate').get('offset').value,
        keyDateGroup.get('issueDate').get('calendar').value,
        keyDateGroup.get('issueDate').get('rollConvention').value
      ),
      interestStartDate: new KeyDateDetail(
        keyDateGroup.get('interestStartDate').get('value').value,
        keyDateGroup.get('interestStartDate').get('priority').value,
        keyDateGroup.get('interestStartDate').get('anchorDate').value,
        keyDateGroup.get('interestStartDate').get('offset').value,
        keyDateGroup.get('interestStartDate').get('calendar').value,
        keyDateGroup.get('interestStartDate').get('rollConvention').value
      ),
      interestEndDate: new KeyDateDetail(
        keyDateGroup.get('interestEndDate').get('value').value,
        keyDateGroup.get('interestEndDate').get('priority').value,
        keyDateGroup.get('interestEndDate').get('anchorDate').value,
        keyDateGroup.get('interestEndDate').get('offset').value,
        keyDateGroup.get('interestEndDate').get('calendar').value,
        keyDateGroup.get('interestEndDate').get('rollConvention').value
      ),
      redemptionDate: new KeyDateDetail(
        keyDateGroup.get('redemptionDate').get('value').value,
        keyDateGroup.get('redemptionDate').get('priority').value,
        keyDateGroup.get('redemptionDate').get('anchorDate').value,
        keyDateGroup.get('redemptionDate').get('offset').value,
        keyDateGroup.get('redemptionDate').get('calendar').value,
        keyDateGroup.get('redemptionDate').get('rollConvention').value
      ),
      initialObservationDate: new KeyDateDetail(
        keyDateGroup.get('initialObservationDate').get('value').value,
        keyDateGroup.get('initialObservationDate').get('priority').value,
        keyDateGroup.get('initialObservationDate').get('anchorDate').value,
        keyDateGroup.get('initialObservationDate').get('offset').value,
        keyDateGroup.get('initialObservationDate').get('calendar').value,
        keyDateGroup.get('initialObservationDate').get('rollConvention').value
      ),
      finalObservationDate: new KeyDateDetail(
        keyDateGroup.get('finalObservationDate').get('value').value,
        keyDateGroup.get('finalObservationDate').get('priority').value,
        keyDateGroup.get('finalObservationDate').get('anchorDate').value,
        keyDateGroup.get('finalObservationDate').get('offset').value,
        keyDateGroup.get('finalObservationDate').get('calendar').value,
        keyDateGroup.get('finalObservationDate').get('rollConvention').value
      ),
      tradeDate: new KeyDateDetail(
        keyDateGroup.get('tradeDate').get('value').value,
        keyDateGroup.get('tradeDate').get('priority').value,
        keyDateGroup.get('tradeDate').get('anchorDate').value,
        keyDateGroup.get('tradeDate').get('offset').value,
        keyDateGroup.get('tradeDate').get('calendar').value,
        keyDateGroup.get('tradeDate').get('rollConvention').value
      ),
      productFinalisationDate: new KeyDateDetail(
        keyDateGroup.get('productFinalisationDate').get('value').value,
        keyDateGroup.get('productFinalisationDate').get('priority').value,
        keyDateGroup.get('productFinalisationDate').get('anchorDate').value,
        keyDateGroup.get('productFinalisationDate').get('offset').value,
        keyDateGroup.get('productFinalisationDate').get('calendar').value,
        keyDateGroup.get('productFinalisationDate').get('rollConvention').value
      ),
    };
  }

  get schedules(): QueryList<ScheduleComponent> {
    return this.eventScheduleComponent?.scheduleComponents;
  }
}
